import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, money, parseAR, formatAR } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Plus, X, RotateCcw, Info, Save, FolderOpen, FileText } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { MonthYearField, DateField } from "@/components/DatePickers";
import { StaleDataDialog } from "@/components/StaleDataDialog";
import { CrossReplaceDialog } from "@/components/CrossReplaceDialog";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const TIPOS_CONCEPTO = [
  ["remunerativo", "Remunerativo"],
  ["nr_solo", "No remunerativo solamente"],
  ["nr_os_sind", "No rem. base de OS y sindicato"],
  ["nr_os", "No rem. sólo base de OS"],
  ["nr_sind", "No rem. sólo base de sindicato"],
];
const fdate = (s) => (s ? s.split("-").reverse().join("/") : "—");
const diasDelMes = (mes, anio) => new Date(Number(anio), Number(mes), 0).getDate();

export default function SueldosPage() {
  const now = new Date();
  const [legajos, setLegajos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [refdata, setRefdata] = useState(null);
  const [form, setForm] = useState({
    legajoId: "", convenio: "comercio", categoria: "",
    aportesPeriodo: "", aportesFecha: "",
    mes: now.getMonth() + 1, anio: now.getFullYear(), quincena: "M",
    modo: "dias", cantidad: String(diasDelMes(now.getMonth() + 1, now.getFullYear())), diasMes: String(diasDelMes(now.getMonth() + 1, now.getFullYear())), horasMes: "",
    artPct: "", antiguedadAnios: "", asistencia: true,
    asistenciaPctOtro: 0.0833, antiguedadPctOtro: 0.01,
    vidriera: false, cajero: "ninguno", basicoManual: "",
  });
  const [feriadosResp, setFeriadosResp] = useState({});
  const [staleRef, setStaleRef] = useState(null);
  const [nuevosFeriados, setNuevosFeriados] = useState([]);
  const [conceptosExtra, setConceptosExtra] = useState([]);
  const [retencionesExtra, setRetencionesExtra] = useState([]);
  const [contribucionesExtra, setContribucionesExtra] = useState([]);
  const [ov, setOv] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saved, setSaved] = useState(null);
  const [sp, setSp] = useSearchParams();

  const legajoSel = useMemo(() => legajos.find((l) => l.id === form.legajoId) || null, [legajos, form.legajoId]);
  const esComercio = form.convenio === "comercio";
  const porHora = form.modo === "horas";

  const [usageInfo, setUsageInfo] = useState({ enabled: false, usage: {} });
  const [crossSel, setCrossSel] = useState(null);
  const fetchUsage = () => api.get("/legajos/usage-status").then((r) => setUsageInfo(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/legajos").then((r) => setLegajos(r.data)).catch(() => {});
    api.get("/sueldos/categorias").then((r) => setCategorias(r.data)).catch(() => {});
    fetchUsage();
  }, []);

  useEffect(() => {
    if (form.anio === "" || Number(form.anio) < 1990) return;
    api.get(`/sueldos/refdata?mes=${form.mes}&anio=${form.anio}`).then((r) => {
      setRefdata(r.data);
      setNuevosFeriados([]);
      setFeriadosResp((prev) => {
        const next = {};
        (r.data.feriados || []).forEach((f) => { next[f.fecha] = prev[f.fecha] || false; });
        return next;
      });
    }).catch(() => {});
  }, [form.mes, form.anio]);

  const selectLegajo = (v) => {
    const id = v === "none" ? "" : v;
    const u = usageInfo.enabled && id && id !== form.legajoId ? usageInfo.usage[id] : null;
    if (u === "aguinaldo" || u === "ambos") { setCrossSel(v); return; }
    applyLegajo(v);
  };
  const applyLegajo = (v) => {
    const id = v === "none" ? "" : v;
    const lg = legajos.find((l) => l.id === id);
    let antig = "";
    if (lg?.fechaIngreso) {
      const fi = String(lg.fechaIngreso).slice(0, 10);
      const yi = Number(fi.slice(0, 4)), mi = Number(fi.slice(5, 7));
      antig = String(Math.max(Math.floor(((form.anio - yi) * 12 + (form.mes - mi)) / 12), 0));
    }
    const porHoraLg = lg?.formaCobro === "por_hora" && (lg?.tipoConvenio || "comercio") !== "comercio";
    const hsNorm = lg?.horasNormalesMes ? formatAR(lg.horasNormalesMes) : "";
    setForm((f) => {
      const dm = String(diasDelMes(f.mes, f.anio));
      return {
        ...f, legajoId: id,
        convenio: lg?.tipoConvenio || f.convenio,
        categoria: lg?.categoria || "",
        artPct: lg?.empresa?.artPct ? lg.empresa.artPct / 100 : "",
        antiguedadAnios: antig,
        modo: porHoraLg ? "horas" : "dias",
        cantidad: porHoraLg ? hsNorm : dm,
        diasMes: dm,
        horasMes: porHoraLg ? hsNorm : "",
        horasJornada: lg?.horasJornada ? formatAR(lg.horasJornada) : "",
        aportesPeriodo: lg?.aportes?.periodo || "",
        aportesFecha: lg?.aportes?.fecha || "",
        asistencia: true,
        asistenciaPctOtro: 0.0833, antiguedadPctOtro: 0.01,
        vidriera: false, cajero: "ninguno", basicoManual: "",
      };
    });
    setResult(null); setOv({});
    setFeriadosResp((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false])));
    setConceptosExtra([]); setRetencionesExtra([]); setContribucionesExtra([]);
    setSaved(null);
    if (id) api.get(`/sueldos/liquidaciones/${id}`).then((r) => setSaved(r.data)).catch(() => setSaved(null));
  };

  // hidrata todo el formulario desde una liquidación guardada
  const cargarGuardada = (liq) => {
    const inp = liq.inputs || {};
    const numStr = (n) => (n || n === 0) && Number(n) !== 0 ? formatAR(Number(n)) : "";
    setForm((f) => ({
      ...f,
      legajoId: liq.legajoId,
      convenio: inp.convenio || "comercio",
      categoria: inp.categoria || "",
      mes: inp.mes, anio: inp.anio, quincena: inp.quincena || "M",
      modo: inp.modo || "dias",
      cantidad: numStr(inp.cantidad), diasMes: numStr(inp.diasMes), horasMes: numStr(inp.horasMes), horasJornada: numStr(inp.horasJornada),
      artPct: inp.artPct != null ? inp.artPct / 100 : "",
      antiguedadAnios: inp.antiguedadAnios != null ? String(inp.antiguedadAnios) : "",
      asistencia: inp.asistencia !== false,
      asistenciaPctOtro: (inp.asistenciaPctOtro || 0) / 100,
      antiguedadPctOtro: (inp.antiguedadPctOtro || 0) / 100,
      vidriera: !!inp.vidriera, cajero: inp.cajero || "ninguno",
      basicoManual: numStr(inp.basicoManual),
      aportesPeriodo: inp.aportesPeriodo || "",
      aportesFecha: inp.aportesFecha || "",
    }));
    const fr = {};
    (inp.feriados || []).forEach((f) => { fr[f.fecha] = !!f.trabajado; });
    setFeriadosResp(fr);
    const lineas = (arr) => (arr || []).map((l) => ({ ...l, base: numStr(l.base), valor: numStr(l.valor) }));
    setConceptosExtra(lineas(inp.conceptosExtra));
    setRetencionesExtra(lineas(inp.retencionesExtra));
    setContribucionesExtra(lineas(inp.contribucionesExtra));
    const o = {};
    Object.entries(inp.overrides || {}).forEach(([k, v]) => {
      o[k] = { ...(v.base != null ? { base: formatAR(v.base) } : {}), ...(v.pct != null ? { pct: v.pct } : {}) };
    });
    setOv(o);
    setResult(liq.resultado || null);
    setSaved(liq);
    toast.success("Liquidación guardada cargada");
  };

  // deep-link desde Sueldos Lista: /app/sueldos?legajo=...&cargar=1
  useEffect(() => {
    const lid = sp.get("legajo");
    if (!lid || !legajos.length) return;
    if (sp.get("cargar") === "1") {
      api.get(`/sueldos/liquidaciones/${lid}`).then((r) => cargarGuardada(r.data)).catch(() => selectLegajo(lid));
    } else {
      selectLegajo(lid);
    }
    setSp({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legajos]);

  const guardar = async () => {
    if (!form.legajoId) { toast.error("Elegí un legajo para guardar la liquidación"); return; }
    if (!result) { toast.error("Primero tocá Calcular"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sueldos/liquidaciones", buildPayload());
      toast.success(data.replacedAguinaldo ? `Liquidación guardada — se reemplazó el Aguinaldo anterior de este empleado — Neto ${money(data.neto)}` : `Liquidación guardada (reemplaza la anterior) — Neto ${money(data.neto)}`);
      api.get(`/sueldos/liquidaciones/${form.legajoId}`).then((r) => setSaved(r.data)).catch(() => {});
      fetchUsage();
    } catch (err) { if (!handle409(err)) toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const generarRecibo = async () => {
    if (!saved) { toast.error("Primero guardá la liquidación"); return; }
    setGenerating(true);
    try {
      await api.post(`/sueldos/liquidaciones/${saved.legajoId}/recibo`);
      toast.success("Recibo generado — disponible en Recibos");
      api.get(`/sueldos/liquidaciones/${saved.legajoId}`).then((r) => setSaved(r.data)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const setMesAnio = (mes, anio) => {
    setForm((f) => ({ ...f, mes, anio, diasMes: String(diasDelMes(mes, anio)), cantidad: f.modo === "dias" ? String(diasDelMes(mes, anio)) : f.cantidad }));
  };

  const buildPayload = () => {
    const extras = (arr) => arr.map((l) => ({
      descripcion: l.descripcion, tipo: l.tipo || "remunerativo", base: parseAR(l.base),
      modo: l.modo || "porcentaje", valor: parseAR(l.valor), sindical: l.sindical !== false,
    }));
    const overrides = {};
    Object.entries(ov).forEach(([k, o]) => {
      const e = {};
      if (o.base != null && o.base !== "") e.base = parseAR(o.base);
      if (o.pct != null && o.pct !== "") e.pct = Number(o.pct);
      if (Object.keys(e).length) overrides[k] = e;
    });
    return {
      legajoId: form.legajoId || null,
      mes: Number(form.mes), anio: Number(form.anio), quincena: form.quincena,
      modo: form.modo, cantidad: parseAR(form.cantidad), diasMes: parseAR(form.diasMes),
      horasMes: porHora ? parseAR(form.horasMes || form.cantidad) : 0,
      horasJornada: porHora ? parseAR(form.horasJornada) : (form.horasJornada ? parseAR(form.horasJornada) : null),
      artPct: form.artPct === "" ? null : Number(form.artPct) * 100,
      antiguedadAnios: form.antiguedadAnios === "" ? null : parseAR(form.antiguedadAnios),
      asistencia: form.asistencia,
      asistenciaPctOtro: form.asistenciaPctOtro === "" ? 0 : Number(form.asistenciaPctOtro) * 100,
      antiguedadPctOtro: form.antiguedadPctOtro === "" ? 0 : Number(form.antiguedadPctOtro) * 100,
      vidriera: form.vidriera, cajero: form.cajero,
      feriados: (refdata?.feriados || []).map((f) => ({ fecha: f.fecha, nombre: f.nombre, trabajado: feriadosResp[f.fecha] === true })),
      refFingerprint: refdata?.refFingerprint || null,
      basicoManual: parseAR(form.basicoManual),
      aportesPeriodo: form.aportesPeriodo || "",
      aportesFecha: form.aportesFecha || "",
      convenio: form.convenio,
      categoria: form.categoria || null,
      conceptosExtra: extras(conceptosExtra),
      retencionesExtra: extras(retencionesExtra),
      contribucionesExtra: extras(contribucionesExtra),
      overrides,
    };
  };

  const handle409 = (err) => {
    const d = err.response?.data?.detail;
    if (err.response?.status === 409 && d?.code === "REFDATA_UPDATED") { setStaleRef(d); return true; }
    return false;
  };

  const staleRows = (() => {
    if (!staleRef) return [];
    const rows = [];
    const fresh = staleRef.fresh || {};
    const blocks = staleRef.blocks || [];
    if (blocks.includes("escala")) {
      const oldR = (refdata?.escala || []).find((x) => x.categoria === form.categoria);
      const newR = (fresh.escala || []).find((x) => x.categoria === form.categoria);
      if (oldR && newR) {
        [["basico", "Básico"], ["nr1", "Aum. NR 1"], ["nr2", "Aum. NR 2"], ["sumaFija", "Suma Fija"]].forEach(([k, lbl]) => {
          if (Number(oldR[k] || 0) !== Number(newR[k] || 0)) rows.push({ label: `Escala — ${form.categoria}: ${lbl}`, old: money(oldR[k] || 0), nue: money(newR[k] || 0) });
        });
      }
      if (!rows.length) rows.push({ text: "Escala salarial del mes actualizada" });
    }
    if (blocks.includes("flags")) rows.push({ text: `Nombre Suma Fija / switches NR actualizados${fresh.nombreSumaFija ? ` — “${fresh.nombreSumaFija}”` : ""}` });
    if (blocks.includes("adicionales")) {
      [["vidrieras", "Vidrieras"], ["cajeroA", "Cajero A"], ["cajeroB", "Cajero B"], ["cajeroC", "Cajero C"], ["laEstrellaPct", "La Estrella %"], ["inacapFijo", "INACAP"]].forEach(([k, lbl]) => {
        const o = Number(refdata?.adicionales?.[k] || 0), n = Number(fresh.adicionales?.[k] || 0);
        if (o !== n) rows.push({ label: `Adicionales: ${lbl}`, old: money(o), nue: money(n) });
      });
    }
    if (blocks.includes("sparams") || blocks.includes("regParams")) rows.push({ text: "Parámetros de liquidación actualizados" });
    if (blocks.includes("feriados")) {
      const oldSet = new Set((refdata?.feriados || []).map((f) => f.fecha));
      const newList = fresh.feriados || [];
      newList.filter((f) => !oldSet.has(f.fecha)).forEach((f) => rows.push({ text: `+ ${fdate(f.fecha)} — ${f.nombre} (NUEVO: indicá si se trabajó)` }));
      const newSet = new Set(newList.map((f) => f.fecha));
      (refdata?.feriados || []).filter((f) => !newSet.has(f.fecha)).forEach((f) => rows.push({ text: `– ${fdate(f.fecha)} — ${f.nombre} (quitado)` }));
    }
    if (!rows.length) rows.push({ text: "Datos de referencia del mes actualizados" });
    return rows;
  })();

  const applyStaleRef = async () => {
    const fresh = staleRef?.fresh || {};
    const oldDates = new Set((refdata?.feriados || []).map((f) => f.fecha));
    const added = (fresh.feriados || []).filter((f) => !oldDates.has(f.fecha)).map((f) => f.fecha);
    const nextResp = {};
    (fresh.feriados || []).forEach((f) => { nextResp[f.fecha] = added.includes(f.fecha) ? null : (feriadosResp[f.fecha] === true); });
    setRefdata((r) => ({ ...r, escala: fresh.escala || r.escala, adicionales: fresh.adicionales || r.adicionales,
      feriados: fresh.feriados || r.feriados, refFingerprint: fresh.refFingerprint || r.refFingerprint }));
    setFeriadosResp(nextResp);
    setNuevosFeriados(added);
    setStaleRef(null);
    if (added.length) { toast.warning("Indicá si se trabajó el feriado nuevo (marcado en amarillo) y volvé a tocar Calcular"); return; }
    setLoading(true);
    try {
      const p = { ...buildPayload(), refFingerprint: fresh.refFingerprint,
        feriados: (fresh.feriados || []).map((f) => ({ fecha: f.fecha, nombre: f.nombre, trabajado: nextResp[f.fecha] === true })) };
      const { data } = await api.post("/sueldos/calcular", p);
      setResult(data);
      toast.success("Cálculo actualizado con los valores nuevos");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const calcular = async () => {
    if (esComercio && !form.categoria) { toast.error("Elegí la categoría (o un legajo con categoría)"); return; }
    if (!esComercio && !parseAR(form.basicoManual)) { toast.error(porHora ? "Ingresá el valor hora" : "Ingresá el básico"); return; }
    if (Object.values(feriadosResp).some((v) => v === null)) { toast.error("Indicá si se trabajó el feriado nuevo (marcado en amarillo)"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/sueldos/calcular", buildPayload());
      setResult(data);
      if (form.antiguedadAnios === "" && data.inputsResueltos?.antiguedadAnios != null) {
        setForm((f) => ({ ...f, antiguedadAnios: String(data.inputsResueltos.antiguedadAnios) }));
      }
      toast.success("Cálculo actualizado");
    } catch (err) { if (!handle409(err)) toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const setOvField = (key, field, value) => setOv((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  const resetOv = (key) => setOv((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const addLinea = (setter, extra = {}) => setter((prev) => [...prev, { descripcion: "", tipo: "remunerativo", base: "", modo: "porcentaje", valor: "", sindical: true, ...extra }]);
  const setLinea = (setter, i, field, value) => setter((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const delLinea = (setter, i) => setter((prev) => prev.filter((_, idx) => idx !== i));

  const feriadosMes = refdata?.feriados || [];
  const s2 = result?.seccion2?.rows || [];
  const s4 = result?.seccion4;
  const s5 = result?.seccion5;
  const s6 = result?.seccion6;

  return (
    <AppLayout title="Recibo de Sueldos"
      actions={
        <div className="flex gap-2">
          <Button onClick={calcular} disabled={loading} data-testid="sueldo-run" className="rounded-none brand-bg text-white gap-2"><Play size={15} /> Calcular</Button>
          <Button onClick={guardar} disabled={saving || !result || !form.legajoId} variant="outline" data-testid="sueldo-save" className="rounded-none gap-2"><Save size={15} /> Guardar liquidación</Button>
          <Button onClick={generarRecibo} disabled={generating || !saved || saved.reciboGenerado} variant="outline" data-testid="sueldo-gen-recibo" className="rounded-none gap-2">
            <FileText size={15} /> {saved?.reciboGenerado ? "Recibo generado" : "Generar Recibo"}
          </Button>
        </div>
      }>
      <div className="max-w-5xl mx-auto space-y-px bg-border border border-border min-w-0 w-full">
        {/* SECCIÓN 1 */}
        <Sec n="1" title="Datos y parámetros de liquidación">
          <div className="grid md:grid-cols-3 gap-4 items-stretch [&>div]:flex [&>div]:flex-col [&>div]:justify-end">
            <div className="md:col-span-3">
              <Label className="label-tiny">Empleado (Legajo)</Label>
              <Select value={form.legajoId || "none"} onValueChange={selectLegajo}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="sueldo-legajo"><SelectValue placeholder="Sin legajo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin legajo</SelectItem>
                  {legajos.map((l) => {
                    const u = usageInfo.enabled && l.id !== form.legajoId ? usageInfo.usage[l.id] : null;
                    return <SelectItem key={l.id} value={l.id}>{l.legajoNumero ? `${l.legajoNumero} · ` : ""}{l.nombre}{u === "aguinaldo" || u === "ambos" ? " — tiene Aguinaldo (se reemplaza al guardar)" : u === "sueldo" ? " — tiene Sueldo (se reemplaza al guardar)" : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            {saved && (
              <div className="md:col-span-3 flex items-center justify-between gap-3 border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid="sueldo-saved-banner">
                <span>Este legajo tiene una liquidación guardada: <b>{MESES[saved.mes - 1]} {saved.anio}{saved.quincena === "1" ? " — 1ª quincena" : saved.quincena === "2" ? " — 2ª quincena" : ""}</b> · Neto {money(saved.neto)}</span>
                <Button variant="outline" size="sm" data-testid="sueldo-saved-cargar" className="rounded-none gap-1 h-8" onClick={() => cargarGuardada(saved)}><FolderOpen size={13} /> Cargar</Button>
              </div>
            )}
            <ReadF label="Apellido y Nombre" value={legajoSel?.nombre} testid="sueldo-f-nombre" />
            <ReadF label="CUIL" value={legajoSel?.cuil} testid="sueldo-f-cuil" />
            <ReadF label="Fecha de ingreso" value={fdate(legajoSel?.fechaIngreso)} testid="sueldo-f-ingreso" />
            <div>
              <Label className="label-tiny">Quincena</Label>
              <Select value={form.quincena} onValueChange={(v) => setForm({ ...form, quincena: v })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-quincena"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Mensual</SelectItem>
                  <SelectItem value="1">1ª quincena</SelectItem>
                  <SelectItem value="2">2ª quincena</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">Mes a liquidar</Label>
              <Select value={String(form.mes)} onValueChange={(v) => setMesAnio(Number(v), form.anio)}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-mes"><SelectValue /></SelectTrigger>
                <SelectContent>{MESES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">Año</Label>
              <Input type="number" data-testid="sueldo-anio" className="mt-1.5 xls-input" value={form.anio}
                onChange={(e) => { const v = e.target.value; if (v === "") setForm((f) => ({ ...f, anio: "" })); else setMesAnio(form.mes, Number(v)); }}
                onBlur={() => { if (form.anio === "" || Number(form.anio) < 1990) setMesAnio(form.mes, new Date().getFullYear()); }} />
            </div>
            <div>
              <Label className="label-tiny">Tipo CCT (convenio)</Label>
              <Select value={form.convenio} onValueChange={(v) => { setForm({ ...form, convenio: v, ...(v === "comercio" && form.modo === "horas" ? { modo: "dias", cantidad: form.diasMes, horasMes: "" } : {}) }); setResult(null); }}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-convenio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="comercio">Comercio (CCT 130/75)</SelectItem>
                  <SelectItem value="fuera_convenio">Fuera de convenio</SelectItem>
                  <SelectItem value="otro_convenio">Otro convenio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {esComercio ? (
              legajoSel ? <ReadF label="Categoría (del legajo)" value={form.categoria} testid="sueldo-f-categoria" /> : (
                <div>
                  <Label className="label-tiny">Categoría (escala)</Label>
                  <Select value={form.categoria || undefined} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                    <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-categoria"><SelectValue placeholder="Elegir categoría" /></SelectTrigger>
                    <SelectContent className="max-h-72">{categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )
            ) : (
              <div>
                <Label className="label-tiny">{porHora ? "Valor hora ($)" : "Básico ($)"}</Label>
                <ARMoneyInput value={form.basicoManual} onChange={(v) => setForm({ ...form, basicoManual: v })} testid="sueldo-basico-manual" />
              </div>
            )}
            <div>
              <Label className="label-tiny">Trabajado por</Label>
              <Select value={form.modo} onValueChange={(v) => setForm({ ...form, modo: v, cantidad: v === "dias" ? form.diasMes : "" })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-modo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="dias">Días</SelectItem>{!esComercio && <SelectItem value="horas">Horas</SelectItem>}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">{porHora ? "Horas trabajadas" : "Días trabajados"}</Label>
              <ARMoneyInput value={form.cantidad} onChange={(v) => setForm({ ...form, cantidad: v })} testid="sueldo-cantidad" />
            </div>
            {porHora ? (
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="label-tiny">Horas por jornada</Label>
                    <ARMoneyInput value={form.horasJornada} onChange={(v) => setForm({ ...form, horasJornada: v })} testid="sueldo-horasjornada" />
                  </div>
                  <div>
                    <Label className="label-tiny">Días del mes</Label>
                    <ARMoneyInput value={form.diasMes} onChange={(v) => setForm({ ...form, diasMes: v })} testid="sueldo-diasmes" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Hs por jornada: del legajo (editable). Días del mes: bases Dto. 814.</p>
              </div>
            ) : (
              <div>
                <Label className="label-tiny">Días del mes</Label>
                <ARMoneyInput value={form.diasMes} onChange={(v) => setForm({ ...form, diasMes: v })} testid="sueldo-diasmes" />
              </div>
            )}
            <div>
              <Label className="label-tiny">A.R.T. %</Label>
              <div className="flex items-center gap-1 mt-1.5">
                <ARPctInput value={form.artPct} onChange={(v) => setForm({ ...form, artPct: v })} testid="sueldo-artpct" className="xls-input text-right" />
                <span className="text-slate-500">%</span>
              </div>
            </div>
            <div>
              <Label className="label-tiny">Período aportes (mm/aaaa)</Label>
              <MonthYearField testid="sueldo-aportes-periodo" value={form.aportesPeriodo} onChange={(v) => setForm({ ...form, aportesPeriodo: v })} />
            </div>
            <div>
              <Label className="label-tiny">F. pago aportes (dd/mm/aaaa)</Label>
              <DateField testid="sueldo-aportes-fecha" className="mt-1.5 w-full" value={form.aportesFecha} onChange={(v) => setForm({ ...form, aportesFecha: v })} />
            </div>
            <div>
              <Label className="label-tiny">Antigüedad (años)</Label>
              <ARMoneyInput value={form.antiguedadAnios} onChange={(v) => setForm({ ...form, antiguedadAnios: v })} testid="sueldo-antiguedad" />
            </div>
            <div>
              <Label className="label-tiny">Asistencia (presentismo)</Label>
              <Select value={form.asistencia ? "si" : "no"} onValueChange={(v) => setForm({ ...form, asistencia: v === "si" })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-asistencia"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
              </Select>
            </div>
            {!esComercio && (
              <>
                <div>
                  <Label className="label-tiny">Asistencia %</Label>
                  <div className="flex items-center gap-1 mt-1.5">
                    <ARPctInput value={form.asistenciaPctOtro} onChange={(v) => setForm({ ...form, asistenciaPctOtro: v })} testid="sueldo-asist-pct-otro" className="xls-input text-right" />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>
                <div>
                  <Label className="label-tiny">Antigüedad % por año</Label>
                  <div className="flex items-center gap-1 mt-1.5">
                    <ARPctInput value={form.antiguedadPctOtro} onChange={(v) => setForm({ ...form, antiguedadPctOtro: v })} testid="sueldo-antig-pct-otro" className="xls-input text-right" />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>
              </>
            )}
            {esComercio && (
              <>
                <div>
                  <Label className="label-tiny">Adicional Vidriera</Label>
                  <Select value={form.vidriera ? "si" : "no"} onValueChange={(v) => setForm({ ...form, vidriera: v === "si" })}>
                    <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-vidriera"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tiny">Adicional Cajeros</Label>
                  <Select value={form.cajero} onValueChange={(v) => setForm({ ...form, cajero: v })}>
                    <SelectTrigger className="mt-1.5 xls-input" data-testid="sueldo-cajero"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ninguno">Ninguno</SelectItem>
                      <SelectItem value="A">Cajero A</SelectItem>
                      <SelectItem value="B">Cajero B</SelectItem>
                      <SelectItem value="C">Cajero C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          {feriadosMes.length > 0 && (
            <div className="mt-5 border border-border">
              <div className="px-3 py-2 bg-slate-50 label-tiny">Feriados de {MESES[form.mes - 1]} {form.anio} — ¿se trabajó?</div>
              {feriadosMes.map((f) => {
                const esNuevo = nuevosFeriados.includes(f.fecha) && feriadosResp[f.fecha] == null;
                return (
                  <div key={f.fecha} className={`flex items-center justify-between px-3 py-2 border-t border-border text-sm ${esNuevo ? "bg-amber-50" : ""}`} data-testid={esNuevo ? `feriado-row-new-${f.fecha}` : `feriado-row-${f.fecha}`}>
                    <span>{fdate(f.fecha)} — {f.nombre}{esNuevo && <span className="ml-2 text-[10px] font-bold text-amber-700">⚠ NUEVO</span>}</span>
                    <Select value={feriadosResp[f.fecha] == null ? undefined : (feriadosResp[f.fecha] ? "si" : "no")} onValueChange={(v) => setFeriadosResp({ ...feriadosResp, [f.fecha]: v === "si" })}>
                      <SelectTrigger className="w-28 h-8 xls-input" data-testid={`feriado-trabajado-${f.fecha}`}><SelectValue placeholder="— elegí" /></SelectTrigger>
                      <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="si">Sí</SelectItem></SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
          {legajoSel && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="sueldo-legajo-info">
              Del legajo: OSECAC {legajoSel.afiliadoOsecac ? "Sí" : "No"} · Sindicato {legajoSel.afiliadoSindicato ? `Sí (${formatAR(legajoSel.sindicatoPct || 0)}%)` : "No"} · Zona {formatAR(legajoSel.zonaPct || 0)}% · Contrib. {legajoSel.empresa?.contribSegSocial === "18" ? "18%" : "20,40%"} · Hs normales {formatAR(legajoSel.horasNormalesMes || 0)} / Hs convenio {formatAR(legajoSel.empresa?.horasConvenio || 0)}
            </p>
          )}
        </Sec>

        {result?.paramsPeriodo && (
          <p className={`text-xs px-1 ${result.paramsFallback ? "text-amber-700" : "text-slate-600"}`} data-testid="sueldo-params-periodo">
            {result.paramsFallback ? (
              <>⚠ Parámetros regulatorios: no hay versión cargada que cubra <b>{result.paramsPeriodo}</b> — se usó la más cercana anterior (válida desde <b>{result.paramsValidoDesde || "—"}</b>).</>
            ) : (
              <><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />Parámetros regulatorios aplicados: <b>{result.paramsPeriodo}</b> — válido desde <b>{result.paramsValidoDesde || "—"}</b> · hasta <b>{result.paramsValidoHasta || "—"}</b> (Bases, Detracción y alícuotas del período liquidado)</>
            )}
          </p>
        )}

        {/* SECCIÓN 2 */}
        <Sec n="2" title="Remuneraciones del mes">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sueldo-s2-table">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="p-2 label-tiny">Concepto</th>
                  <th className="p-2 label-tiny text-right">Base</th>
                  <th className="p-2 label-tiny text-right">Cant/%</th>
                  <th className="p-2 label-tiny text-right">Remunerativo</th>
                  <th className="p-2 label-tiny text-right">No remunerativo</th>
                  <th className="p-2 label-tiny text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {s2.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Completá la sección 1 y tocá Calcular.</td></tr>}
                {s2.map((row) => (
                  <tr key={row.key} className="border-b border-border/60 text-slate-600" data-testid={`s2-row-${row.key}`}>
                    <td className="p-2">
                      <span className="inline-flex items-center gap-1.5">
                        {row.concepto}
                        {row.nota && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" data-testid={`s2-nota-${row.key}`} className="text-slate-400 hover:text-slate-700 cursor-help" aria-label="Ver nota">
                                  <Info size={13} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs">{row.nota}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </td>
                    <td className="p-2 text-right tabular">{money(row.base)}</td>
                    <td className="p-2 text-right">{row.cantidad}</td>
                    <td className="p-2 text-right tabular">{money(row.remunerativo)}</td>
                    <td className="p-2 text-right tabular">{money(row.noRemunerativo)}</td>
                    <td className="p-2 text-right tabular font-medium">{money(row.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LineasExtra titulo="Conceptos agregados" lineas={conceptosExtra} conTipo sugerencias={refdata?.conceptos || []}
            onAdd={() => addLinea(setConceptosExtra)} onSet={(i, f, v) => setLinea(setConceptosExtra, i, f, v)} onDel={(i) => delLinea(setConceptosExtra, i)} testid="concepto-extra" />
        </Sec>

        {/* SECCIÓN 3 */}
        <Sec n="3" title="Totales de haberes">
          <div className="max-w-md">
            <KV l="Total Remunerativo" v={result ? money(result.seccion3.totalRemunerativo) : "—"} testid="s3-rem" />
            <KV l="Total No Remunerativo" v={result ? money(result.seccion3.totalNoRemunerativo) : "—"} testid="s3-nr" />
            <KV l="TOTAL HABERES" v={result ? money(result.seccion3.totalHaberes) : "—"} testid="s3-haberes" bold />
          </div>
        </Sec>

        {/* SECCIÓN 4 */}
        <Sec n="4" title="Bases imponibles">
          {!s4 ? <p className="text-sm text-muted-foreground">Tocá Calcular para ver las bases.</p> : (
            <div className="grid md:grid-cols-2 gap-x-10">
              {s4.diasProporcionar != null && (
                <>
                  <KV l="Días para proporcionar (hs trab. × días mes ÷ hs convenio, redondeado)" v={String(s4.diasProporcionar)} testid="s4-diasprop" />
                  <KV l="Proporción Dto. 814 (días ÷ días del mes)" v={formatAR(s4.factor814)} testid="s4-factor814" />
                </>
              )}
              <KV l={`Base mínima seg. social (prorr. ×${formatAR(s4.diasProporcionar != null ? s4.factor814 : s4.factorTiempo)})`} v={money(s4.baseMinima)} testid="s4-basemin" />
              <KV l="Base máxima seg. social (prorr.)" v={money(s4.baseMaxima)} testid="s4-basemax" />
              <KV l="Base aportes seg. social (con topes)" v={money(s4.baseSegSocial)} testid="s4-basess" />
              <KV l="Valor base Obra Social (÷hs normales × hs convenio)" v={money(s4.valorObraSocial)} testid="s4-valoros" />
              <KV l="Piso Obra Social (mín × 2)" v={money(s4.pisoObraSocial)} testid="s4-pisoos" />
              <KV l="Base OS trabajador (piso y techo)" v={money(s4.osBaseTrabajador)} testid="s4-ostrab" />
              <KV l="Base OS empleador (solo piso)" v={money(s4.osBaseEmpleador)} testid="s4-osemp" />
              <KV l="Base sindical" v={money(s4.baseSindical)} testid="s4-sind" />
              <KV l="Detracción Dto. 814 (prorrateada)" v={money(s4.detraccion814)} testid="s4-detraccion" />
              <KV l="Base contribuciones (mín − detracción)" v={money(s4.baseContribuciones)} testid="s4-basecontrib" />
            </div>
          )}
        </Sec>

        {/* SECCIÓN 5 */}
        <Sec n="5" title="Retenciones — Aportes del trabajador">
          {!s5 ? <p className="text-sm text-muted-foreground">Tocá Calcular para ver las retenciones.</p> : (
            <table className="w-full text-sm" data-testid="sueldo-s5-table">
              <thead><tr className="border-b border-border text-left"><th className="p-2 label-tiny">Concepto</th><th className="p-2 label-tiny text-right">Base imponible</th><th className="p-2 label-tiny text-right">Alícuota</th><th className="p-2 label-tiny text-right">Importe</th></tr></thead>
              <tbody>
                {s5.rows.map((row) => (
                  <OvRow key={row.key} row={row} ov={ov[row.key]} onBase={(v) => setOvField(row.key, "base", v)} onPct={(v) => setOvField(row.key, "pct", v)} onReset={() => resetOv(row.key)} testid={`s5-${row.key}`} />
                ))}
                <tr className="border-t-2 border-border font-medium text-slate-900">
                  <td className="p-2" colSpan={3}>Total descuentos</td>
                  <td className="p-2 text-right tabular" data-testid="s5-total">{money(s5.totalDescuentos)}</td>
                </tr>
                <tr className="font-semibold text-slate-900 bg-slate-50">
                  <td className="p-2" colSpan={3}>NETO A COBRAR</td>
                  <td className="p-2 text-right tabular" data-testid="s5-neto">{money(s5.neto)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <LineasExtra titulo="Retenciones agregadas" lineas={retencionesExtra} conSindical
            onAdd={() => addLinea(setRetencionesExtra)} onSet={(i, f, v) => setLinea(setRetencionesExtra, i, f, v)} onDel={(i) => delLinea(setRetencionesExtra, i)} testid="retencion-extra" />
          {Object.keys(ov).length > 0 && <p className="text-xs text-amber-700 mt-2">Hay bases/alícuotas editadas: volvé a tocar Calcular para aplicarlas.</p>}
        </Sec>

        {/* SECCIÓN 6 */}
        <Sec n="6" title="Contribuciones patronales">
          {!s6 ? <p className="text-sm text-muted-foreground">Tocá Calcular para ver las contribuciones.</p> : (
            <table className="w-full text-sm" data-testid="sueldo-s6-table">
              <thead><tr className="border-b border-border text-left"><th className="p-2 label-tiny">Concepto</th><th className="p-2 label-tiny text-right">Base imponible</th><th className="p-2 label-tiny text-right">Alícuota</th><th className="p-2 label-tiny text-right">Importe</th></tr></thead>
              <tbody>
                {s6.rows.map((row) => (
                  <OvRow key={row.key} row={row} ov={ov[row.key]} fijo={row.fijo} onBase={(v) => setOvField(row.key, "base", v)} onPct={(v) => setOvField(row.key, "pct", v)} onReset={() => resetOv(row.key)} testid={`s6-${row.key}`} />
                ))}
                <tr className="border-t-2 border-border font-medium text-slate-900">
                  <td className="p-2" colSpan={3}>Total contribuciones</td>
                  <td className="p-2 text-right tabular" data-testid="s6-total">{money(s6.totalContribuciones)}</td>
                </tr>
                <tr className="font-semibold text-slate-900 bg-slate-50">
                  <td className="p-2" colSpan={3}>COSTO TOTAL EMPLEADOR (haberes + contribuciones)</td>
                  <td className="p-2 text-right tabular" data-testid="s6-costototal">{money(s6.costoTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <LineasExtra titulo="Contribuciones agregadas" lineas={contribucionesExtra}
            onAdd={() => addLinea(setContribucionesExtra)} onSet={(i, f, v) => setLinea(setContribucionesExtra, i, f, v)} onDel={(i) => delLinea(setContribucionesExtra, i)} testid="contribucion-extra" />
        </Sec>
      </div>
      <CrossReplaceDialog
        open={!!crossSel}
        otherLabel="Aguinaldo"
        currentLabel="Recibo de Sueldos"
        onConfirm={() => { applyLegajo(crossSel); setCrossSel(null); }}
        onCancel={() => setCrossSel(null)}
      />
      <StaleDataDialog
        open={!!staleRef}
        title="Datos actualizados por el administrador"
        intro={`Mientras trabajabas se actualizaron valores que afectan esta liquidación (${MESES[form.mes - 1]} ${form.anio}).`}
        rows={staleRows}
        note="Todo lo que cargaste (días, horas, conceptos, respuestas de los demás feriados) se conserva."
        buttonText={(staleRef?.blocks || []).includes("feriados") ? "Actualizar valores y continuar" : "Actualizar valores y recalcular"}
        onApply={applyStaleRef}
        applying={loading}
      />
    </AppLayout>
  );
}

// fila S5/S6 con base y alícuota editables (override) + reset
function OvRow({ row, ov, fijo, onBase, onPct, onReset, testid }) {
  const esFijo = row.pctDefault === 0 && row.base === 0;
  const baseShown = ov?.base != null ? ov.base : formatAR(row.base);
  const pctShown = ov?.pct != null ? ov.pct : row.pct / 100;
  return (
    <tr className="border-b border-border/60 text-slate-600" data-testid={`${testid}-row`}>
      <td className="p-2">{row.concepto}{fijo > 0 ? <span className="text-xs text-slate-400"> (incluye fijo {money(fijo)})</span> : null}</td>
      <td className="p-1 text-right">
        {esFijo ? <span className="tabular">—</span> : (
          <div className="flex items-center gap-1 justify-end">
            <ARMoneyInput value={baseShown} onChange={onBase} testid={`${testid}-base`} className="xls-input text-right h-8 w-40" />
            {ov && <button type="button" title="Volver al valor calculado" data-testid={`${testid}-reset`} onClick={onReset} className="text-slate-400 hover:text-slate-700"><RotateCcw size={13} /></button>}
          </div>
        )}
      </td>
      <td className="p-1 text-right">
        {esFijo ? <span>fijo</span> : (
          <div className="flex items-center gap-1 justify-end">
            <ARPctInput value={pctShown} onChange={onPct} testid={`${testid}-pct`} className="xls-input text-right h-8 w-24" />
            <span className="text-slate-500">%</span>
          </div>
        )}
      </td>
      <td className="p-2 text-right tabular" data-testid={`${testid}-importe`}>{money(row.importe)}</td>
    </tr>
  );
}

function LineasExtra({ titulo, lineas, conTipo, conSindical, sugerencias = [], onAdd, onSet, onDel, testid }) {
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <p className="label-tiny">{titulo}</p>
        <Button variant="outline" size="sm" onClick={onAdd} data-testid={`${testid}-add`} className="rounded-none gap-1 h-8"><Plus size={13} /> Agregar</Button>
      </div>
      {lineas.map((ln, i) => (
        <div key={i} className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-amber-50/40 border border-border p-2" data-testid={`${testid}-${i}`}>
          <div className="col-span-2">
            <Label className="label-tiny">Descripción</Label>
            <Input list={sugerencias.length ? `${testid}-sug` : undefined} className="xls-input h-8 mt-1" data-testid={`${testid}-${i}-desc`} value={ln.descripcion} onChange={(e) => onSet(i, "descripcion", e.target.value)} />
          </div>
          {conTipo && (
            <div className="col-span-2">
              <Label className="label-tiny">Tipo</Label>
              <Select value={ln.tipo} onValueChange={(v) => onSet(i, "tipo", v)}>
                <SelectTrigger className="xls-input h-8 mt-1" data-testid={`${testid}-${i}-tipo`}><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_CONCEPTO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-tiny">Base ($)</Label>
            <ARMoneyInput value={ln.base} onChange={(v) => onSet(i, "base", v)} testid={`${testid}-${i}-base`} className="xls-input text-right h-8 mt-1" />
          </div>
          <div>
            <Label className="label-tiny">Modo</Label>
            <Select value={ln.modo} onValueChange={(v) => onSet(i, "modo", v)}>
              <SelectTrigger className="xls-input h-8 mt-1" data-testid={`${testid}-${i}-modo`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="porcentaje">%</SelectItem><SelectItem value="cantidad">Cantidad</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tiny">{ln.modo === "cantidad" ? "Cantidad" : "%"}</Label>
            <ARMoneyInput value={ln.valor} onChange={(v) => onSet(i, "valor", v)} testid={`${testid}-${i}-valor`} className="xls-input text-right h-8 mt-1" />
          </div>
          <div className="flex items-center gap-2">
            {conSindical && (
              <div className="flex-1">
                <Label className="label-tiny">Sindical</Label>
                <Select value={ln.sindical !== false ? "si" : "no"} onValueChange={(v) => onSet(i, "sindical", v === "si")}>
                  <SelectTrigger className="xls-input h-8 mt-1" data-testid={`${testid}-${i}-sindical`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <button type="button" onClick={() => onDel(i)} data-testid={`${testid}-${i}-del`} className="text-destructive hover:opacity-70 mb-2"><X size={15} /></button>
          </div>
        </div>
      ))}
      {sugerencias.length > 0 && (
        <datalist id={`${testid}-sug`}>{sugerencias.map((c) => <option key={c.id || c.nombre} value={c.nombre} />)}</datalist>
      )}
      {lineas.length > 0 && <p className="text-xs text-muted-foreground mt-1">Tocá Calcular para incorporar las líneas al cálculo.</p>}
    </div>
  );
}

const Sec = ({ n, title, children }) => (
  <section className="bg-white">
    <div className="px-5 py-3 brand-bg"><span className="label-tiny label-on-dark">{n ? `${n}) ` : ""}{title}</span></div>
    <div className="p-5">{children}</div>
  </section>
);
const ReadF = ({ label, value, testid }) => (
  <div>
    <Label className="label-tiny">{label}</Label>
    <div className="mt-1.5 h-10 px-3 flex items-center border border-border bg-slate-50 text-sm text-slate-900" data-testid={testid}>{value || "—"}</div>
  </div>
);
const KV = ({ l, v, testid, bold }) => (
  <div className={`flex justify-between gap-4 py-1.5 border-b border-border text-sm ${bold ? "font-semibold text-slate-900" : ""}`}>
    <span className="text-slate-600">{l}</span><span className="tabular font-medium text-slate-900 whitespace-nowrap" data-testid={testid}>{v}</span>
  </div>
);
const ARMoneyInput = ({ value, onChange, testid, className, disabled }) => {
  const handleChange = (e) => { const v = e.target.value; if (/^[0-9.,]*$/.test(v)) onChange(v); };
  const handleBlur = () => onChange(value === "" ? "" : formatAR(parseAR(value)));
  return (
    <Input type="text" inputMode="decimal" placeholder="0,00" data-testid={testid} disabled={disabled}
      className={className || "xls-input text-right mt-1.5"} value={value ?? ""} onChange={handleChange} onBlur={handleBlur} />
  );
};
const ARPctInput = ({ value, onChange, testid, disabled, className }) => {
  const [draft, setDraft] = useState(null);
  const formatted = value === "" || value == null ? ""
    : Number((Number(value) * 100).toFixed(4)).toLocaleString("es-AR", { maximumFractionDigits: 4, useGrouping: false });
  const shown = draft != null ? draft : formatted;
  return (
    <Input type="text" inputMode="decimal" data-testid={testid} disabled={disabled} className={className}
      title={shown === "" ? "" : `${shown}%`} value={shown}
      onChange={(e) => { const v = e.target.value; if (/^[0-9.,]*$/.test(v)) { setDraft(v); onChange(v === "" ? "" : parseAR(v) / 100); } }}
      onBlur={() => setDraft(null)} />
  );
};
